import React, { useState } from 'react'
import tw from 'twin.macro'

// Components
import { Link, graphql } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import Layout from '../components/Layout'
import {
  ButtonStyle,
  CopyLGStyle,
  CopyStyle,
  HeaderLGStyle,
  HeaderStyle,
  LinkButton,
  ScoreStyle,
  SubheadSMStyle,
} from '../components/UI'
import { Tab } from '@headlessui/react'
import { Menu } from '@headlessui/react'
import FrequentlyAskedQuestions from '../components/FrequentlyAskedQuestions'
import CallToAction from '../components/CallToAction'
import Star from '../components/Star'
import Testimonials from '../components/Testimonials'
import Features from '../components/Features'
import Seo from '../components/Seo'
import { useForm } from 'react-hook-form'
import { css } from '@emotion/react'
import appStore from '../images/app-store.svg'
import googlePlay from '../images/google-play.svg'

const StartEarning = ({ location, data }) => {
  const pageData = data.contentfulStartEarningPage
  const globalData = data.contentfulGlobalSettings

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [activeDropdownItem, setActiveDropdownItem] = useState('Toronto')

  const [formSubmitted, setFormSubmitted] = useState(false)
  const topForm = useForm()
  const bottomForm = useForm()

  const onSubmit = (data) => {
    const params = {
      lookingFor: data.need,
      name: data.name,
      email: data.email,
      need: 'Looking for work',
      industry: 'Hospitality',
    }
    const options = {
      method: 'POST',
      body: JSON.stringify(params),
    }
    fetch('https://hooks.zapier.com/hooks/catch/4090899/bq2g86d/', options)
      .then((response) => response.json())
      .then((response) => {
        if (response.status === 'success' && !formSubmitted) {
          setFormSubmitted(true)

          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'Form Submit',
              eventLabel: 'Work, Healthcare - Form Submit',
              field: 'Start Earning',
            })
          }
        }
      })
  }

  return (
    <Layout location={location}>
      <Seo
        title={pageData.metadataTitle || 'Start Earning'}
        description={pageData.metadataDescription}
        image={
          pageData.metadataImage
            ? `https:${pageData.metadataImage.file.url}`
            : undefined
        }
      />
      {/* Hero */}
      <section tw="text-[#EFEFEF] bg-[#202C3A]">
        <div tw="flex flex-col lg:(grid grid-cols-[1fr repeat(12, calc(1024px/12)) 1fr] relative h-[575px])">
          {/* Content */}
          <div tw="order-1 mx-7 mb-9 lg:(mx-0 mb-0 order-2 col-start-8 col-span-5) flex flex-col justify-center">
            <h2 css={[HeaderLGStyle, tw`mt-8 mb-6 text-[#C2E1EA]`]}>
              {pageData.heroHeadline}
            </h2>
            <div css={[CopyStyle, tw`flex font-medium mb-[22px]`]}>
              <a
                href="https://staffy-live.netlify.app/"
                tw="text-[rgba(32, 44, 58, 0.3)] px-[12px] py-[6px] border border-[#FF4D00] bg-[#F1FAFD] mr-[28px] "
              >
                Healthcare
              </a>
              <div tw="bg-[#C2E1EA] border border-[#C2E1EA] text-[rgba(32, 44, 58, 0.3)] px-[12px] py-[6px]">
                <span>Hospitality</span>
              </div>
            </div>
            <p css={[CopyLGStyle, tw`mb-12 w-3/4 lg:w-full`]}>
              {pageData.heroSubhead.heroSubhead}
            </p>
            <div>
              <LinkButton
                destination="/app"
                tw="inline-block py-3 w-full text-center border-[#FF4D00] border-[1.5px] lg:(w-auto px-8)"
              >
                {pageData.heroCta}
              </LinkButton>
            </div>
          </div>
          {/* Image */}
          <div tw="order-2 lg:(order-1 col-span-6 relative h-full)">
            <GatsbyImage
              tw="w-full lg:(absolute w-full h-full object-cover)"
              image={pageData.heroImage.gatsbyImageData}
              alt={pageData.heroImage.filename}
            />
          </div>
        </div>
      </section>

      {/* Average worker rating */}
      <section tw="bg-[#F1FAFD]">
        <div tw="flex flex-col py-12 lg:(py-24 grid grid-cols-[1fr repeat(12, calc(1024px/12)) 1fr])">
          {/* Copy */}
          <div tw="mx-7 mb-8 lg:(mx-0 mb-0 col-start-2 col-span-5 flex flex-col justify-center)">
            <div css={[SubheadSMStyle, tw`mb-8 lg:mb-4`]}>
              {pageData.section1Headline}
            </div>
            <h2 css={[HeaderLGStyle, tw`mb-4 lg:mb-6`]}>
              {pageData.section1Subhead}
            </h2>
            <p css={[CopyLGStyle]}>{pageData.section1Copy.section1Copy}</p>
          </div>
          {/* Rating */}
          <div tw="bg-white mx-7 py-10 pl-0 lg:(mx-0 pl-16 py-16 col-start-9 col-span-6)">
            {/* Score header */}
            <div
              css={[
                ScoreStyle,
                tw`text-[#FF4D00] mb-2.5 text-center lg:text-left`,
              ]}
            >
              {pageData.section1AverageWorkerRating}/5
            </div>
            {/* Stars */}
            <div tw="flex justify-center mb-7 lg:(block mb-4)">
              {/* Fully filled stars */}
              {[
                ...Array(
                  Math.floor(pageData.section1AverageWorkerRating)
                ).keys(),
              ].map((i, index) => (
                <Star
                  id={`star-filled-${index}`}
                  percentage="100"
                  key={`star-filled-${index}`}
                />
              ))}
              {/* Partially filled stars */}
              <Star
                id={`star-partial`}
                percentage={Math.round(
                  (pageData.section1AverageWorkerRating % 1) * 100
                )}
                key={`star-partial`}
              />
              {/* Empty stars */}
              {[
                ...Array(
                  5 - Math.ceil(pageData.section1AverageWorkerRating)
                ).keys(),
              ].map((i, index) => (
                <Star
                  id={`star-empty-${index}`}
                  percentage="0"
                  key={`star-empty-${index}`}
                />
              ))}
            </div>
            {/* Subhead */}
            <div
              css={[
                CopyLGStyle,
                tw`text-[#FF4D00] text-center px-16 lg:(text-left px-0)`,
              ]}
            >
              average worker rating
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section tw="mx-7 pt-[100px] pb-[50px] lg:(max-w-screen-xl mx-auto)">
        <form
          onSubmit={topForm.handleSubmit(onSubmit)}
          tw="grid grid-cols-4 lg:(grid-cols-[1fr repeat(12, calc(1024px/12)) 1fr] relative)"
        >
          <div tw="col-span-4 lg:(col-start-2 col-span-5 pr-10)">
            <div css={[tw`block`, formSubmitted && tw`hidden`]}>
              <h2 css={[HeaderStyle, tw`mb-[24px]`]}>
                How can Staffy&nbsp;help&nbsp;you?
              </h2>
              <p css={[CopyLGStyle, tw`mb-[30px]`]}>
                Fill out this form and a member of our team will reach out
                with more information to help you get started with Staffy.
              </p>
            </div>
            <div css={[tw`hidden`, formSubmitted && tw`block`]}>
              <h2 css={[HeaderStyle, tw`mb-[24px]`]}>Thank you!</h2>
              <p css={[CopyLGStyle, tw`mb-[30px]`]}>
                We will be in touch shortly.
              </p>
              <div css={[CopyStyle, tw`mb-4`]}>Download the app</div>
              {/* App links */}
              <div tw="mb-10 lg:(mb-9)">
                {globalData.iosDownloadLink && (
                  <a
                    href={globalData.iosDownloadLink}
                    target="_blank"
                    rel="noreferrer"
                    tw="inline-block mr-2.5 w-[123px]"
                  >
                    <img src={appStore} />
                  </a>
                )}
                {globalData.androidDownloadLink && (
                  <a
                    href={globalData.androidDownloadLink}
                    target="_blank"
                    rel="noreferrer"
                    tw="inline-block w-[123px]"
                  >
                    <img src={googlePlay} />
                  </a>
                )}
              </div>
            </div>
          </div>
          <div css={[tw`col-span-4 lg:(col-start-7 col-span-7)`]}>
            <div css={[formSubmitted && tw`invisible`]}>
              {/* Name */}
              <div tw="mb-10">
                <label css={[SubheadSMStyle, tw`block mb-3.5`]} htmlFor="Name">
                  Full Name*
                </label>
                <div className="input-group">
                  <input
                    css={[CopyStyle, tw`border border-black w-full px-7 py-7`]}
                    type="text"
                    id="Name"
                    name="Name"
                    placeholder="Type your name"
                    {...topForm.register('name', { required: true })}
                  />
                  {topForm.formState.errors.name && (
                    <span css={[CopyStyle, tw`text-red-500`]}>
                      This field is required
                    </span>
                  )}
                </div>
              </div>
              {/* Email */}
              <div tw="mb-10">
                <label css={[SubheadSMStyle, tw`block mb-3.5`]} htmlFor="Email">
                  Email*
                </label>
                <div className="input-group">
                  <input
                    css={[CopyStyle, tw`border border-black w-full px-7 py-7`]}
                    type="email"
                    id="Email"
                    name="Email"
                    placeholder="Type your email"
                    {...topForm.register('email', {
                      required: true,
                      pattern:
                        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                    })}
                  />
                  {topForm.formState.errors.email && (
                    <span css={[CopyStyle, tw`text-red-500`]}>
                      This field is required
                    </span>
                  )}
                </div>
              </div>
              {/* Submit */}
              <div>
                <input
                  type="submit"
                  value="Submit"
                  css={[
                    ButtonStyle,
                    tw`bg-[#FF4D00] text-white inline-block py-7 px-8 w-full text-center border-[#FF4D00] border-[1.5px] cursor-pointer lg:(px-16 w-auto)`,
                    tw`transition-all transition-duration[0.2s] transition-timing-function[cubic-bezier(0.645,0.045,0.355,1)] hover:(text-[#202C3A] bg-white)`,
                  ]}
                />
              </div>
            </div>
          </div>
        </form>
      </section>

      {/* Job postings */}
      {false && (
        <section tw="bg-[#F1FAFD] py-11 px-7 lg:px-0">
          <h2 css={[HeaderStyle, tw`text-left mb-2.5 lg:(text-center mb-11)`]}>
            {pageData.section2Headline}
          </h2>
          <div tw="flex flex-col lg:(grid grid-cols-[1fr repeat(12, calc(1024px/12)) 1fr])">
            <div tw="col-start-2 col-span-12">
              {/* Dropdown */}
              <Menu as="div" tw="lg:hidden">
                <Menu.Button tw="text-[#FF4D00]">
                  {activeDropdownItem}
                </Menu.Button>
                <Menu.Items tw="absolute bg-white border border-black">
                  <Menu.Item
                    onClick={() => {
                      setSelectedIndex(0)
                      setActiveDropdownItem('Toronto')
                    }}
                  >
                    {({ active }) => (
                      <div css={[tw`my-2 px-7 py-4`]}>Toronto</div>
                    )}
                  </Menu.Item>
                  <Menu.Item
                    onClick={() => {
                      setSelectedIndex(1)
                      setActiveDropdownItem('New York')
                    }}
                  >
                    {({ active }) => (
                      <div css={[tw`my-2 px-7 py-4`]}>New York</div>
                    )}
                  </Menu.Item>
                  <Menu.Item
                    onClick={() => {
                      setSelectedIndex(2)
                      setActiveDropdownItem('Vancouver')
                    }}
                  >
                    {({ active }) => (
                      <div css={[tw`my-2 px-7 py-4`]}>Vancouver</div>
                    )}
                  </Menu.Item>
                  <Menu.Item
                    onClick={() => {
                      setSelectedIndex(3)
                      setActiveDropdownItem('Montreal')
                    }}
                  >
                    {({ active }) => (
                      <div css={[tw`my-2 px-7 py-4`]}>Montreal</div>
                    )}
                  </Menu.Item>
                  <Menu.Item
                    onClick={() => {
                      setSelectedIndex(4)
                      setActiveDropdownItem('Ottawa')
                    }}
                  >
                    {({ active }) => {
                      return <div css={[tw`my-2 px-7 py-4`]}>Ottawa</div>
                    }}
                  </Menu.Item>
                </Menu.Items>
              </Menu>
              {/* Tab Group */}
              <Tab.Group
                selectedIndex={selectedIndex}
                onChange={setSelectedIndex}
              >
                <Tab.List tw="hidden lg:(flex justify-between items-center)">
                  <Tab>Toronto</Tab>
                  <Tab>New York</Tab>
                  <Tab>Vancouver</Tab>
                  <Tab>Montreal</Tab>
                  <Tab>Ottawa</Tab>
                </Tab.List>
                <Tab.Panels tw="my-8 lg:my-14">
                  <Tab.Panel>
                    {/* Job */}
                    <Link to="/">
                      <div tw="flex flex-col border border-black px-3.5 py-5 mb-4 lg:(flex-row justify-between items-center px-8 py-7)">
                        {/* Job info */}
                        <div tw="mb-2 lg:(mb-0 flex)">
                          <h3 css={[SubheadSMStyle]}>Job title lorem ipsum</h3>
                          <div css={[CopyStyle, tw`ml-3.5 hidden lg:block`]}>
                            Toronto, ON
                          </div>
                        </div>
                        {/* Job rate */}
                        <div>
                          <div css={[CopyStyle, tw`inline-block lg:hidden`]}>
                            Toronto, ON |&nbsp;
                          </div>
                          <div css={[CopyStyle, tw`inline-block`]}>
                            $60 CAD / <span tw="hidden lg:inline">hour</span>
                            <span tw="lg:hidden">hr</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                    <Link to="/">
                      <div tw="flex flex-col border border-black px-3.5 py-5 mb-4 lg:(flex-row justify-between items-center px-8 py-7)">
                        {/* Job info */}
                        <div tw="mb-2 lg:(mb-0 flex)">
                          <h3 css={[SubheadSMStyle]}>Job title lorem ipsum</h3>
                          <div css={[CopyStyle, tw`ml-3.5 hidden lg:block`]}>
                            Toronto, ON
                          </div>
                        </div>
                        {/* Job rate */}
                        <div>
                          <div css={[CopyStyle, tw`inline-block lg:hidden`]}>
                            Toronto, ON |&nbsp;
                          </div>
                          <div css={[CopyStyle, tw`inline-block`]}>
                            $60 CAD / <span tw="hidden lg:inline">hour</span>
                            <span tw="lg:hidden">hr</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                    <Link to="/">
                      <div tw="flex flex-col border border-black px-3.5 py-5 mb-4 lg:(flex-row justify-between items-center px-8 py-7)">
                        {/* Job info */}
                        <div tw="mb-2 lg:(mb-0 flex)">
                          <h3 css={[SubheadSMStyle]}>Job title lorem ipsum</h3>
                          <div css={[CopyStyle, tw`ml-3.5 hidden lg:block`]}>
                            Toronto, ON
                          </div>
                        </div>
                        {/* Job rate */}
                        <div>
                          <div css={[CopyStyle, tw`inline-block lg:hidden`]}>
                            Toronto, ON |&nbsp;
                          </div>
                          <div css={[CopyStyle, tw`inline-block`]}>
                            $60 CAD / <span tw="hidden lg:inline">hour</span>
                            <span tw="lg:hidden">hr</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                    <Link to="/">
                      <div tw="flex flex-col border border-black px-3.5 py-5 mb-4 lg:(flex-row justify-between items-center px-8 py-7)">
                        {/* Job info */}
                        <div tw="mb-2 lg:(mb-0 flex)">
                          <h3 css={[SubheadSMStyle]}>Job title lorem ipsum</h3>
                          <div css={[CopyStyle, tw`ml-3.5 hidden lg:block`]}>
                            Toronto, ON
                          </div>
                        </div>
                        {/* Job rate */}
                        <div>
                          <div css={[CopyStyle, tw`inline-block lg:hidden`]}>
                            Toronto, ON |&nbsp;
                          </div>
                          <div css={[CopyStyle, tw`inline-block`]}>
                            $60 CAD / <span tw="hidden lg:inline">hour</span>
                            <span tw="lg:hidden">hr</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                    <Link to="/">
                      <div tw="flex flex-col border border-black px-3.5 py-5 mb-4 lg:(flex-row justify-between items-center px-8 py-7)">
                        {/* Job info */}
                        <div tw="mb-2 lg:(mb-0 flex)">
                          <h3 css={[SubheadSMStyle]}>Job title lorem ipsum</h3>
                          <div css={[CopyStyle, tw`ml-3.5 hidden lg:block`]}>
                            Toronto, ON
                          </div>
                        </div>
                        {/* Job rate */}
                        <div>
                          <div css={[CopyStyle, tw`inline-block lg:hidden`]}>
                            Toronto, ON |&nbsp;
                          </div>
                          <div css={[CopyStyle, tw`inline-block`]}>
                            $60 CAD / <span tw="hidden lg:inline">hour</span>
                            <span tw="lg:hidden">hr</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </Tab.Panel>
                </Tab.Panels>
              </Tab.Group>
              <div tw="flex justify-center">
                <LinkButton destination="/jobs">
                  {pageData.section2Cta}
                </LinkButton>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Features of our app */}
      <Features />

      {/* Testimonials */}
      {pageData.testimonialsTestimonials && (
        <section tw="bg-[#F1FAFD] py-7 lg:(pt-16) overflow-hidden">
          <div tw="mx-7 lg:(max-w-screen-lg mx-auto relative)">
            <h2 css={[HeaderStyle, tw`mb-9`]}>
              {pageData.testimonialsHeadline}
            </h2>
            <Testimonials testimonials={pageData.testimonialsTestimonials} />
          </div>
        </section>
      )}

      {/* FAQ */}
      <FrequentlyAskedQuestions
        headline={pageData.faqHeadline}
        subhead={pageData.faqSubhead.faqSubhead}
        questions={pageData.faqQuestions}
        image={pageData.faqImage}
      />

      {/* Form */}
      <section tw="mx-7 pt-[100px] pb-[100px] lg:(max-w-screen-xl mx-auto)">
        <form
          onSubmit={bottomForm.handleSubmit(onSubmit)}
          tw="grid grid-cols-4 lg:(grid-cols-[1fr repeat(12, calc(1024px/12)) 1fr] relative)"
        >
          <div tw="col-span-4 lg:(col-start-2 col-span-5 pr-10)">
            <div css={[tw`block`, formSubmitted && tw`hidden`]}>
              <h2 css={[HeaderStyle, tw`mb-[24px]`]}>
                How can Staffy&nbsp;help&nbsp;you?
              </h2>
              <p css={[CopyLGStyle, tw`mb-[30px]`]}>
                Fill out this form and a member of our team will reach out
                with more information to help you get started with Staffy.
              </p>
            </div>
            <div css={[tw`hidden`, formSubmitted && tw`block`]}>
              <h2 css={[HeaderStyle, tw`mb-[24px]`]}>Thank you!</h2>
              <p css={[CopyLGStyle, tw`mb-[30px]`]}>
                We will be in touch shortly.
              </p>
              <div css={[CopyStyle, tw`mb-4`]}>Download the app</div>
              {/* App links */}
              <div tw="mb-10 lg:(mb-9)">
                {globalData.iosDownloadLink && (
                  <a
                    href={globalData.iosDownloadLink}
                    target="_blank"
                    rel="noreferrer"
                    tw="inline-block mr-2.5 w-[123px]"
                  >
                    <img src={appStore} />
                  </a>
                )}
                {globalData.androidDownloadLink && (
                  <a
                    href={globalData.androidDownloadLink}
                    target="_blank"
                    rel="noreferrer"
                    tw="inline-block w-[123px]"
                  >
                    <img src={googlePlay} />
                  </a>
                )}
              </div>
            </div>
          </div>
          <div css={[tw`col-span-4 lg:(col-start-7 col-span-7)`]}>
            <div css={[formSubmitted && tw`invisible`]}>
              {/* Name */}
              <div tw="mb-10">
                <label css={[SubheadSMStyle, tw`block mb-3.5`]} htmlFor="Name">
                  Full Name*
                </label>
                <div className="input-group">
                  <input
                    css={[CopyStyle, tw`border border-black w-full px-7 py-7`]}
                    type="text"
                    id="Name"
                    name="Name"
                    placeholder="Type your name"
                    {...bottomForm.register('name', { required: true })}
                  />
                  {bottomForm.formState.errors.name && (
                    <span css={[CopyStyle, tw`text-red-500`]}>
                      This field is required
                    </span>
                  )}
                </div>
              </div>
              {/* Email */}
              <div tw="mb-10">
                <label css={[SubheadSMStyle, tw`block mb-3.5`]} htmlFor="Email">
                  Email*
                </label>
                <div className="input-group">
                  <input
                    css={[CopyStyle, tw`border border-black w-full px-7 py-7`]}
                    type="email"
                    id="Email"
                    name="Email"
                    placeholder="Type your email"
                    {...bottomForm.register('email', {
                      required: true,
                      pattern:
                        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                    })}
                  />
                  {bottomForm.formState.errors.email && (
                    <span css={[CopyStyle, tw`text-red-500`]}>
                      This field is required
                    </span>
                  )}
                </div>
              </div>
              {/* Submit */}
              <div>
                <input
                  type="submit"
                  value="Submit"
                  css={[
                    ButtonStyle,
                    tw`bg-[#FF4D00] text-white inline-block py-7 px-8 w-full text-center border-[#FF4D00] border-[1.5px] cursor-pointer lg:(px-16 w-auto)`,
                    tw`transition-all transition-duration[0.2s] transition-timing-function[cubic-bezier(0.645,0.045,0.355,1)] hover:(text-[#202C3A] bg-white)`,
                  ]}
                />
              </div>
            </div>
          </div>
        </form>
      </section>

      {/* CTA */}
      <CallToAction
        header={pageData.ctaHeadline}
        copy={pageData.ctaSubhead.ctaSubhead}
        ctas={[{ copy: 'Start Earning', destination: '/app' }]}
        image={pageData.ctaImage}
      />
    </Layout>
  )
}

export default StartEarning

export const pageQuery = graphql`
  {
    contentfulStartEarningPage(pageId: { eq: "Start Earning" }) {
      heroHeadline
      heroSubhead {
        heroSubhead
      }
      heroCta
      heroImage {
        filename
        gatsbyImageData(
          layout: CONSTRAINED
          placeholder: DOMINANT_COLOR
          width: 1024
          quality: 80
        )
      }
      section1Headline
      section1Subhead
      section1Copy {
        section1Copy
      }
      section1AverageWorkerRating
      section2Headline
      section2Cta
      testimonialsHeadline
      testimonialsTestimonials {
        copy {
          copy
        }
        name
        initials
        role
      }
      faqImage {
        filename
        gatsbyImageData(
          layout: CONSTRAINED
          placeholder: DOMINANT_COLOR
          width: 1024
          quality: 80
        )
      }
      faqHeadline
      faqSubhead {
        faqSubhead
      }
      faqQuestions {
        question
        answer {
          raw
        }
      }
      ctaHeadline
      ctaSubhead {
        ctaSubhead
      }
      ctaImage {
        filename
        gatsbyImageData(
          layout: CONSTRAINED
          placeholder: DOMINANT_COLOR
          width: 1024
          quality: 80
        )
      }
      metadataTitle
      metadataDescription
      metadataImage {
        file {
          url
        }
      }
    }

    contentfulGlobalSettings(pageId: { eq: "Globals" }) {
      iosDownloadLink
      androidDownloadLink
    }
  }
`
